// SPDX-License-Identifier: AGPL-3.0-or-later

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {makeAutoObservable, runInAction} from 'mobx';

/**
 * KONVO: per-device tweaks for the glass theme. The theme itself is a normal
 * user setting (synced through UserSettings); the knobs below are hardware
 * and taste dependent, so they stay local to the device like zoom or motion.
 */

export const GlassBackdropStyles = {
	AURORA: 'aurora',
	MESH: 'mesh',
	PHOTO: 'photo',
	FLAT: 'flat',
	IMAGE: 'image',
} as const;
export type GlassBackdropStyle = (typeof GlassBackdropStyles)[keyof typeof GlassBackdropStyles];

export const GlassRailShapes = {
	ROUNDED: 'rounded',
	CIRCLE: 'circle',
} as const;
export type GlassRailShape = (typeof GlassRailShapes)[keyof typeof GlassRailShapes];

export const GlassTones = {
	DARK: 'dark',
	LIGHT: 'light',
	SYSTEM: 'system',
} as const;
export type GlassTone = (typeof GlassTones)[keyof typeof GlassTones];

/** Three wallpaper colours as #rrggbb. */
export type GlassWallpaperColors = readonly [string, string, string];

export const GlassPalettes = {
	KONVO: 'konvo',
	STUDIO: 'studio',
	SUNRISE: 'sunrise',
	OCEAN: 'ocean',
	FOREST: 'forest',
	EMBER: 'ember',
	MONO: 'mono',
} as const;
export type GlassPalette = (typeof GlassPalettes)[keyof typeof GlassPalettes];

export const GLASS_PALETTE_COLORS: Record<GlassPalette, GlassWallpaperColors> = {
	konvo: ['#5b48ff', '#c02fbf', '#1fb6ff'],
	studio: ['#7c5cff', '#2aa198', '#e0834f'],
	sunrise: ['#ff9a5c', '#9a7bff', '#5aa9ff'],
	ocean: ['#2fb8c9', '#3b6cf0', '#7bd4ff'],
	forest: ['#3fa66b', '#9bd15a', '#2b8a78'],
	ember: ['#ff6b4a', '#ff3d7f', '#ffb454'],
	mono: ['#8a8fa8', '#6b7089', '#a9adc2'],
};

/** Where the three glows sit. */
export const GlassWallpaperLayouts = {
	CORNERS: 'corners',
	STUDIO: 'studio',
	HORIZON: 'horizon',
	CENTRE: 'centre',
} as const;
export type GlassWallpaperLayout = (typeof GlassWallpaperLayouts)[keyof typeof GlassWallpaperLayouts];

/** One-click looks: every knob at once. */
export const GlassLooks = {
	MIST_MONO: 'mist_mono',
	MIST_STUDIO: 'mist_studio',
	MIST_KONVO: 'mist_konvo',
	SMOKED_GRAPHITE: 'smoked_graphite',
	SMOKED_STUDIO: 'smoked_studio',
	STUDIO_DARK: 'studio_dark',
	STUDIO_LIGHT: 'studio_light',
	SMOKED: 'smoked',
} as const;
export type GlassLook = (typeof GlassLooks)[keyof typeof GlassLooks];

export const GlassPresets = {
	SMOKED: 'smoked',
	BALANCED: 'balanced',
	SUBTLE: 'subtle',
	FROSTED: 'frosted',
	CLEAR: 'clear',
} as const;
export type GlassPreset = (typeof GlassPresets)[keyof typeof GlassPresets];

export interface GlassThemeSettings {
	/** Backdrop blur radius in CSS pixels. */
	blur: number;
	/** Panel tint opacity, 0–100. */
	surfaceOpacity: number;
	/** Panel edge highlight opacity, 0–100. */
	borderGlow: number;
	/** Corner radius of floating panels in CSS pixels. */
	cornerRadius: number;
	backdrop: GlassBackdropStyle;
	/** Light or dark material, or follow the OS. */
	tone: GlassTone;
	/** The three wallpaper colours. */
	wallpaperColors: GlassWallpaperColors;
	/** How strongly the colours show through, 0–100. */
	wallpaperIntensity: number;
	/** How far the colour glows are smeared, 0–100. */
	wallpaperSoftness: number;
	/** Canvas brightness offset, -50–50. */
	wallpaperBrightness: number;
	wallpaperLayout: GlassWallpaperLayout;
	/** Film grain over the wallpaper, 0–100. */
	wallpaperGrain: number;
	/** Darkened edges, 0–100. */
	wallpaperVignette: number;
	/** Shift the colours and brightness with the time of day. */
	wallpaperDaylight: boolean;
	/** Look used for the light tone, and for "match system" when the OS is light. */
	lightLook: GlassLook;
	/** Look used for the dark tone, and for "match system" when the OS is dark. */
	darkLook: GlassLook;
	/** Shape of the tiles in the left rail. Rounded follows cornerRadius. */
	railShape: GlassRailShape;
	floatingPanels: boolean;
	animateBackdrop: boolean;
	reduceTransparency: boolean;
}

export const GLASS_LIMITS = {
	blur: {min: 0, max: 60},
	surfaceOpacity: {min: 10, max: 100},
	borderGlow: {min: 0, max: 40},
	cornerRadius: {min: 0, max: 28},
	wallpaperIntensity: {min: 0, max: 100},
	wallpaperSoftness: {min: 0, max: 100},
	wallpaperBrightness: {min: -50, max: 50},
	wallpaperGrain: {min: 0, max: 100},
	wallpaperVignette: {min: 0, max: 100},
} as const;

const PRESET_VALUES: Record<
	GlassPreset,
	Pick<GlassThemeSettings, 'blur' | 'surfaceOpacity' | 'borderGlow' | 'cornerRadius'>
> = {
	smoked: {blur: 36, surfaceOpacity: 70, borderGlow: 10, cornerRadius: 16},
	balanced: {blur: 18, surfaceOpacity: 42, borderGlow: 14, cornerRadius: 14},
	subtle: {blur: 10, surfaceOpacity: 70, borderGlow: 8, cornerRadius: 10},
	frosted: {blur: 32, surfaceOpacity: 55, borderGlow: 22, cornerRadius: 18},
	clear: {blur: 8, surfaceOpacity: 22, borderGlow: 18, cornerRadius: 16},
};

export const GLASS_DEFAULT_SETTINGS: GlassThemeSettings = {
	// New devices start on Smoked graphite (dark) with Mist studio waiting for the light tone.
	blur: 36,
	surfaceOpacity: 70,
	borderGlow: 8,
	cornerRadius: 22,
	backdrop: GlassBackdropStyles.AURORA,
	tone: GlassTones.DARK,
	wallpaperColors: GLASS_PALETTE_COLORS.mono,
	wallpaperIntensity: 35,
	wallpaperSoftness: 50,
	wallpaperBrightness: -6,
	wallpaperLayout: GlassWallpaperLayouts.CORNERS,
	wallpaperGrain: 0,
	wallpaperVignette: 20,
	wallpaperDaylight: false,
	lightLook: 'mist_studio',
	darkLook: 'smoked_graphite',
	railShape: GlassRailShapes.ROUNDED,
	floatingPanels: true,
	// Motion stays available in Appearance; a stationary wallpaper avoids continuous idle rendering.
	animateBackdrop: false,
	reduceTransparency: false,
};

const BACKDROP_STYLES = new Set<string>(Object.values(GlassBackdropStyles));
const RAIL_SHAPES = new Set<string>(Object.values(GlassRailShapes));
const TONES = new Set<string>(Object.values(GlassTones));
const LAYOUTS = new Set<string>(Object.values(GlassWallpaperLayouts));
const LOOKS = new Set<string>(Object.values(GlassLooks));

/** The tone a look belongs to. */
export function glassLookTone(look: GlassLook): typeof GlassTones.LIGHT | typeof GlassTones.DARK {
	return GLASS_LOOK_VALUES[look]?.tone === GlassTones.LIGHT ? GlassTones.LIGHT : GlassTones.DARK;
}

/** Settings a look never overrides: the user's Fine-tune choices, saved on this device. */
const FINE_TUNE_KEYS = ['blur', 'floatingPanels', 'reduceTransparency', 'animateBackdrop'] as const;

/** A look's values minus the Fine-tune settings (and minus the tone when asked). */
function lookKnobs(look: GlassLook, {keepTone}: {keepTone: boolean}): Partial<GlassThemeSettings> {
	const values: Partial<GlassThemeSettings> = {...GLASS_LOOK_VALUES[look]};
	for (const key of FINE_TUNE_KEYS) delete values[key];
	if (keepTone) delete values.tone;
	return values;
}

function osPrefersDark(): boolean {
	return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

/** Mist and Smoked (2026-09): quiet, low-contrast looks with softened wallpapers and no orange. */
const MIST_STUDIO_COLORS: GlassWallpaperColors = ['#8b74ff', '#3aa99c', '#d9a58c'];
const SMOKED_STUDIO_COLORS: GlassWallpaperColors = ['#7c5cff', '#2aa198', '#b98468'];

/** One-click looks. Each belongs to a tone; Mist and Smoked are the defaults for light and dark. */
export const GLASS_LOOK_VALUES: Record<GlassLook, Partial<GlassThemeSettings>> = {
	mist_mono: {
		tone: GlassTones.LIGHT,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: GLASS_PALETTE_COLORS.mono,
		wallpaperIntensity: 45,
		wallpaperSoftness: 60,
		wallpaperBrightness: 4,
		wallpaperLayout: GlassWallpaperLayouts.CORNERS,
		wallpaperGrain: 0,
		wallpaperVignette: 0,
		blur: 30,
		surfaceOpacity: 62,
		borderGlow: 18,
		cornerRadius: 22,
		floatingPanels: true,
	},
	mist_studio: {
		tone: GlassTones.LIGHT,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: MIST_STUDIO_COLORS,
		wallpaperIntensity: 55,
		wallpaperSoftness: 55,
		wallpaperBrightness: 2,
		wallpaperLayout: GlassWallpaperLayouts.CORNERS,
		wallpaperGrain: 0,
		wallpaperVignette: 0,
		blur: 30,
		surfaceOpacity: 62,
		borderGlow: 18,
		cornerRadius: 22,
		floatingPanels: true,
	},
	mist_konvo: {
		tone: GlassTones.LIGHT,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: GLASS_PALETTE_COLORS.konvo,
		wallpaperIntensity: 45,
		wallpaperSoftness: 60,
		wallpaperBrightness: 2,
		wallpaperLayout: GlassWallpaperLayouts.CORNERS,
		wallpaperGrain: 0,
		wallpaperVignette: 0,
		blur: 30,
		surfaceOpacity: 62,
		borderGlow: 18,
		cornerRadius: 22,
		floatingPanels: true,
	},
	smoked_graphite: {
		tone: GlassTones.DARK,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: GLASS_PALETTE_COLORS.mono,
		wallpaperIntensity: 35,
		wallpaperSoftness: 50,
		wallpaperBrightness: -6,
		wallpaperLayout: GlassWallpaperLayouts.CORNERS,
		wallpaperGrain: 0,
		wallpaperVignette: 20,
		blur: 36,
		surfaceOpacity: 70,
		borderGlow: 8,
		cornerRadius: 22,
		floatingPanels: true,
	},
	smoked_studio: {
		tone: GlassTones.DARK,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: SMOKED_STUDIO_COLORS,
		wallpaperIntensity: 60,
		wallpaperSoftness: 45,
		wallpaperBrightness: 0,
		wallpaperLayout: GlassWallpaperLayouts.STUDIO,
		wallpaperGrain: 6,
		wallpaperVignette: 30,
		blur: 32,
		surfaceOpacity: 64,
		borderGlow: 10,
		cornerRadius: 22,
		floatingPanels: true,
	},
	studio_dark: {
		tone: GlassTones.DARK,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: GLASS_PALETTE_COLORS.studio,
		wallpaperIntensity: 80,
		wallpaperSoftness: 35,
		wallpaperBrightness: 6,
		wallpaperLayout: GlassWallpaperLayouts.STUDIO,
		wallpaperGrain: 12,
		wallpaperVignette: 45,
		blur: 30,
		surfaceOpacity: 58,
		borderGlow: 10,
		cornerRadius: 18,
		floatingPanels: true,
	},
	studio_light: {
		tone: GlassTones.LIGHT,
		backdrop: GlassBackdropStyles.AURORA,
		wallpaperColors: GLASS_PALETTE_COLORS.studio,
		wallpaperIntensity: 85,
		wallpaperSoftness: 45,
		wallpaperBrightness: 0,
		wallpaperLayout: GlassWallpaperLayouts.STUDIO,
		wallpaperGrain: 6,
		wallpaperVignette: 0,
		blur: 30,
		surfaceOpacity: 62,
		borderGlow: 18,
		cornerRadius: 18,
		floatingPanels: true,
	},
	smoked: {
		tone: GlassTones.DARK,
		backdrop: GlassBackdropStyles.FLAT,
		wallpaperColors: GLASS_PALETTE_COLORS.konvo,
		wallpaperIntensity: 70,
		wallpaperSoftness: 40,
		wallpaperBrightness: 0,
		wallpaperLayout: GlassWallpaperLayouts.CORNERS,
		wallpaperGrain: 0,
		wallpaperVignette: 0,
		blur: 36,
		surfaceOpacity: 70,
		borderGlow: 10,
		cornerRadius: 16,
		floatingPanels: true,
	},
};
const HEX_COLOR = /^#[0-9a-f]{6}$/i;

function normalizeColors(value: unknown, fallback: GlassWallpaperColors): GlassWallpaperColors {
	if (!Array.isArray(value) || value.length !== 3) return fallback;
	const colors = value.map((entry, index) =>
		typeof entry === 'string' && HEX_COLOR.test(entry) ? entry.toLowerCase() : fallback[index]!,
	);
	return [colors[0]!, colors[1]!, colors[2]!];
}

function clamp(value: number, min: number, max: number): number {
	if (!Number.isFinite(value)) return min;
	return Math.min(max, Math.max(min, Math.round(value)));
}

class GlassTheme {
	blur = GLASS_DEFAULT_SETTINGS.blur;
	surfaceOpacity = GLASS_DEFAULT_SETTINGS.surfaceOpacity;
	borderGlow = GLASS_DEFAULT_SETTINGS.borderGlow;
	cornerRadius = GLASS_DEFAULT_SETTINGS.cornerRadius;
	backdrop: GlassBackdropStyle = GLASS_DEFAULT_SETTINGS.backdrop;
	tone: GlassTone = GLASS_DEFAULT_SETTINGS.tone;
	wallpaperColors: GlassWallpaperColors = GLASS_DEFAULT_SETTINGS.wallpaperColors;
	wallpaperIntensity = GLASS_DEFAULT_SETTINGS.wallpaperIntensity;
	wallpaperSoftness = GLASS_DEFAULT_SETTINGS.wallpaperSoftness;
	wallpaperBrightness = GLASS_DEFAULT_SETTINGS.wallpaperBrightness;
	wallpaperLayout: GlassWallpaperLayout = GLASS_DEFAULT_SETTINGS.wallpaperLayout;
	wallpaperGrain = GLASS_DEFAULT_SETTINGS.wallpaperGrain;
	wallpaperVignette = GLASS_DEFAULT_SETTINGS.wallpaperVignette;
	wallpaperDaylight = GLASS_DEFAULT_SETTINGS.wallpaperDaylight;
	lightLook: GlassLook = GLASS_DEFAULT_SETTINGS.lightLook;
	darkLook: GlassLook = GLASS_DEFAULT_SETTINGS.darkLook;
	railShape: GlassRailShape = GLASS_DEFAULT_SETTINGS.railShape;
	floatingPanels = GLASS_DEFAULT_SETTINGS.floatingPanels;
	animateBackdrop = GLASS_DEFAULT_SETTINGS.animateBackdrop;
	reduceTransparency = GLASS_DEFAULT_SETTINGS.reduceTransparency;
	private _hydrated = false;
	/** The OS colour scheme as the app sees it; Theme pushes it in (it also knows the Linux portal). */
	private systemDark = osPrefersDark();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
	}

	/**
	 * Called by Theme whenever its view of the OS scheme changes. With "match system" the look
	 * remembered for the new scheme comes in.
	 */
	setSystemDark(dark: boolean): void {
		if (this.systemDark === dark) return;
		this.systemDark = dark;
		if (this._hydrated && this.tone === GlassTones.SYSTEM) this.applyLookValues(dark ? this.darkLook : this.lightLook);
	}

	get isHydrated(): boolean {
		return this._hydrated;
	}

	get settings(): GlassThemeSettings {
		return {
			blur: this.blur,
			surfaceOpacity: this.surfaceOpacity,
			borderGlow: this.borderGlow,
			cornerRadius: this.cornerRadius,
			backdrop: this.backdrop,
			tone: this.tone,
			wallpaperColors: this.wallpaperColors,
			wallpaperIntensity: this.wallpaperIntensity,
			wallpaperSoftness: this.wallpaperSoftness,
			wallpaperBrightness: this.wallpaperBrightness,
			wallpaperLayout: this.wallpaperLayout,
			wallpaperGrain: this.wallpaperGrain,
			wallpaperVignette: this.wallpaperVignette,
			wallpaperDaylight: this.wallpaperDaylight,
			lightLook: this.lightLook,
			darkLook: this.darkLook,
			railShape: this.railShape,
			floatingPanels: this.floatingPanels,
			animateBackdrop: this.animateBackdrop,
			reduceTransparency: this.reduceTransparency,
		};
	}

	/** The preset whose sliders match the current values, if any. */
	get activePreset(): GlassPreset | null {
		for (const [name, values] of Object.entries(PRESET_VALUES) as Array<
			[GlassPreset, (typeof PRESET_VALUES)[GlassPreset]]
		>) {
			if (
				values.blur === this.blur &&
				values.surfaceOpacity === this.surfaceOpacity &&
				values.borderGlow === this.borderGlow &&
				values.cornerRadius === this.cornerRadius
			) {
				return name;
			}
		}
		return null;
	}

	/** The palette whose colours match the current wallpaper, if any. */
	get activePalette(): GlassPalette | null {
		for (const [name, colors] of Object.entries(GLASS_PALETTE_COLORS) as Array<[GlassPalette, GlassWallpaperColors]>) {
			if (colors.every((color, index) => color === this.wallpaperColors[index])) return name;
		}
		return null;
	}

	/** The look whose values all match, if any. */
	get activeLook(): GlassLook | null {
		for (const [name, values] of Object.entries(GLASS_LOOK_VALUES) as Array<[GlassLook, Partial<GlassThemeSettings>]>) {
			const matches = (Object.keys(values) as Array<keyof GlassThemeSettings>).every((key) =>
				key === 'tone'
					? this.tone === GlassTones.SYSTEM || this.tone === values.tone
					: key === 'wallpaperColors'
						? values.wallpaperColors!.every((color, index) => color === this.wallpaperColors[index])
						: this.settings[key] === values[key],
			);
			if (matches) return name;
		}
		return null;
	}

	get isDefault(): boolean {
		const defaults = GLASS_DEFAULT_SETTINGS;
		const current = this.settings;
		return (Object.keys(defaults) as Array<keyof GlassThemeSettings>).every((key) =>
			key === 'wallpaperColors'
				? defaults.wallpaperColors.every((color, index) => color === current.wallpaperColors[index])
				: current[key] === defaults[key],
		);
	}

	update(partial: Partial<GlassThemeSettings>): void {
		if (partial.blur !== undefined) this.blur = clamp(partial.blur, GLASS_LIMITS.blur.min, GLASS_LIMITS.blur.max);
		if (partial.surfaceOpacity !== undefined) {
			this.surfaceOpacity = clamp(
				partial.surfaceOpacity,
				GLASS_LIMITS.surfaceOpacity.min,
				GLASS_LIMITS.surfaceOpacity.max,
			);
		}
		if (partial.borderGlow !== undefined) {
			this.borderGlow = clamp(partial.borderGlow, GLASS_LIMITS.borderGlow.min, GLASS_LIMITS.borderGlow.max);
		}
		if (partial.cornerRadius !== undefined) {
			this.cornerRadius = clamp(partial.cornerRadius, GLASS_LIMITS.cornerRadius.min, GLASS_LIMITS.cornerRadius.max);
		}
		if (partial.backdrop !== undefined && BACKDROP_STYLES.has(partial.backdrop)) this.backdrop = partial.backdrop;
		if (partial.lightLook !== undefined && LOOKS.has(partial.lightLook)) this.lightLook = partial.lightLook;
		if (partial.darkLook !== undefined && LOOKS.has(partial.darkLook)) this.darkLook = partial.darkLook;
		if (partial.tone !== undefined && TONES.has(partial.tone)) this.tone = partial.tone;
		if (partial.wallpaperColors !== undefined) {
			this.wallpaperColors = normalizeColors(partial.wallpaperColors, this.wallpaperColors);
		}
		if (partial.wallpaperIntensity !== undefined) {
			this.wallpaperIntensity = clamp(
				partial.wallpaperIntensity,
				GLASS_LIMITS.wallpaperIntensity.min,
				GLASS_LIMITS.wallpaperIntensity.max,
			);
		}
		if (partial.wallpaperSoftness !== undefined) {
			this.wallpaperSoftness = clamp(
				partial.wallpaperSoftness,
				GLASS_LIMITS.wallpaperSoftness.min,
				GLASS_LIMITS.wallpaperSoftness.max,
			);
		}
		if (partial.wallpaperBrightness !== undefined) {
			this.wallpaperBrightness = clamp(
				partial.wallpaperBrightness,
				GLASS_LIMITS.wallpaperBrightness.min,
				GLASS_LIMITS.wallpaperBrightness.max,
			);
		}
		if (partial.wallpaperLayout !== undefined && LAYOUTS.has(partial.wallpaperLayout)) {
			this.wallpaperLayout = partial.wallpaperLayout;
		}
		if (partial.wallpaperGrain !== undefined) {
			this.wallpaperGrain = clamp(
				partial.wallpaperGrain,
				GLASS_LIMITS.wallpaperGrain.min,
				GLASS_LIMITS.wallpaperGrain.max,
			);
		}
		if (partial.wallpaperVignette !== undefined) {
			this.wallpaperVignette = clamp(
				partial.wallpaperVignette,
				GLASS_LIMITS.wallpaperVignette.min,
				GLASS_LIMITS.wallpaperVignette.max,
			);
		}
		if (partial.wallpaperDaylight !== undefined) this.wallpaperDaylight = Boolean(partial.wallpaperDaylight);
		if (partial.railShape !== undefined && RAIL_SHAPES.has(partial.railShape)) this.railShape = partial.railShape;
		if (partial.floatingPanels !== undefined) this.floatingPanels = Boolean(partial.floatingPanels);
		if (partial.animateBackdrop !== undefined) this.animateBackdrop = Boolean(partial.animateBackdrop);
		if (partial.reduceTransparency !== undefined) this.reduceTransparency = Boolean(partial.reduceTransparency);
	}

	/**
	 * Pick a look. It is remembered for its tone. With an explicit tone the look also sets the tone;
	 * with "match system" it only shows now when the OS is in that look's scheme.
	 */
	applyLook(look: GlassLook): void {
		if (!GLASS_LOOK_VALUES[look]) return;
		const tone = glassLookTone(look);
		this.update(tone === GlassTones.LIGHT ? {lightLook: look} : {darkLook: look});
		if (this.tone === GlassTones.SYSTEM) {
			if ((tone === GlassTones.DARK) === this.systemDark) this.applyLookValues(look);
			return;
		}
		this.update(lookKnobs(look, {keepTone: false}));
	}

	/** Switch tone and bring in the look remembered for it. */
	setTone(tone: GlassTone): void {
		if (!TONES.has(tone)) return;
		this.update({tone});
		const dark = tone === GlassTones.SYSTEM ? this.systemDark : tone === GlassTones.DARK;
		this.applyLookValues(dark ? this.darkLook : this.lightLook);
	}

	/**
	 * Bring in a look's wallpaper and material without touching the tone. A chosen photo stays,
	 * as do the Fine-tune settings (see lookKnobs).
	 */
	private applyLookValues(look: GlassLook): void {
		const values = lookKnobs(look, {keepTone: true});
		if (this.backdrop === GlassBackdropStyles.IMAGE) delete values.backdrop;
		this.update(values);
	}

	applyPalette(palette: GlassPalette): void {
		const colors = GLASS_PALETTE_COLORS[palette];
		if (colors) this.update({wallpaperColors: colors});
	}

	applyPreset(preset: GlassPreset): void {
		const values = PRESET_VALUES[preset];
		if (!values) return;
		this.update(values);
	}

	reset(): void {
		this.update(GLASS_DEFAULT_SETTINGS);
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(
			this,
			'GlassTheme',
			[
				'blur',
				'surfaceOpacity',
				'borderGlow',
				'cornerRadius',
				'backdrop',
				'tone',
				'wallpaperColors',
				'wallpaperIntensity',
				'wallpaperSoftness',
				'wallpaperBrightness',
				'wallpaperLayout',
				'wallpaperGrain',
				'wallpaperVignette',
				'wallpaperDaylight',
				'lightLook',
				'darkLook',
				'railShape',
				'floatingPanels',
				'animateBackdrop',
				'reduceTransparency',
			],
			{syncAcrossTabs: true},
		);
		runInAction(() => {
			// Re-run the clamps in case an older or hand-edited snapshot was restored.
			this.update(this.settings);
			this._hydrated = true;
			// The OS may have switched light/dark while the app was closed.
			if (this.tone === GlassTones.SYSTEM) this.applyLookValues(this.systemDark ? this.darkLook : this.lightLook);
		});
	}
}

export default new GlassTheme();
